import React, { useState, useRef, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  IconButton,
  CircularProgress,
  Avatar,
  Box,
  Typography
} from "@material-ui/core";
import {
  PlayArrow,
  Pause,
  Mic,
  Person
} from "@material-ui/icons";

import { getBackendUrl } from "../../config";

const LS_NAME = "audioMessageRate";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    alignItems: "center",
    padding: "6px 8px 4px 6px",
    minWidth: 260,
    maxWidth: 320,
    userSelect: "none",
  },
  avatarWrapper: {
    position: "relative",
    marginRight: 10,
    flexShrink: 0,
  },
  avatar: {
    width: 44,
    height: 44,
    backgroundColor: theme.palette.type === "dark" ? "#2a3942" : "#dfe5e7",
    color: theme.palette.type === "dark" ? "#aebac1" : "#54656f",
  },
  micBadge: {
    position: "absolute",
    bottom: -2,
    right: -2,
    width: 18,
    height: 18,
    borderRadius: "50%",
    backgroundColor: "#00a884",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
    "& svg": {
      fontSize: 12,
      color: "#ffffff",
    },
  },
  playButton: {
    padding: 6,
    marginRight: 6,
    color: theme.palette.type === "dark" ? "#aebac1" : "#54656f",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.05)",
    },
  },
  playIcon: {
    fontSize: 32,
  },
  contentWrapper: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    justifyContent: "center",
    minWidth: 0,
  },
  waveformContainer: {
    display: "flex",
    alignItems: "center",
    height: 28,
    cursor: "pointer",
    padding: "4px 0",
    position: "relative",
  },
  waveformBar: {
    width: 3,
    marginRight: 2,
    borderRadius: 2,
    transition: "background-color 0.1s ease",
  },
  bottomRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 2,
  },
  durationText: {
    fontSize: "0.72rem",
    color: theme.palette.type === "dark" ? "#8696a0" : "#667781",
    fontWeight: 500,
    lineHeight: 1,
  },
  speedButton: {
    fontSize: "0.7rem",
    fontWeight: 700,
    lineHeight: 1,
    padding: "2px 6px",
    borderRadius: 10,
    cursor: "pointer",
    backgroundColor: theme.palette.type === "dark" ? "#2a3942" : "#e9edef",
    color: theme.palette.type === "dark" ? "#00a884" : "#008069",
    border: "none",
    outline: "none",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "#374248" : "#d1d7db",
    },
  },
}));

// Generates consistent pseudo-random bar heights from the audio URL string
const generateBarHeights = (seedStr, count = 28) => {
  let hash = 0;
  for (let i = 0; i < seedStr.length; i++) {
    hash = (hash << 5) - hash + seedStr.charCodeAt(i);
    hash |= 0;
  }

  const heights = [];
  const baseHeights = [
    6, 12, 18, 10, 22, 16, 8, 24, 14, 18,
    26, 12, 20, 10, 24, 16, 14, 22, 8, 18,
    26, 14, 10, 20, 16, 12, 22, 8, 14, 18
  ];

  for (let i = 0; i < count; i++) {
    const pseudoRand = Math.abs(Math.sin((hash + i) * 999));
    const base = baseHeights[i % baseHeights.length];
    const h = Math.round(6 + pseudoRand * 18);
    heights.push(Math.max(5, Math.min(24, Math.round((base + h) / 2))));
  }
  return heights;
};

const formatAudioTime = (seconds) => {
  if (isNaN(seconds) || seconds === null || seconds === Infinity) {
    return "0:00";
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
};

export default function Audio({ url, fromMe, contact, autor, taxa, onTaxa }) {
  const classes = useStyles();
  const audioRef = useRef(null);
  const waveformRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [duration, setDuration] = useState(0);
  const medindoRef = useRef(false);
  const [currentTime, setCurrentTime] = useState(0);







  const [taxaLocal, setTaxaLocal] = useState(
    parseFloat(localStorage.getItem(LS_NAME) || "1")
  );

  const compartilhada = typeof taxa === "number";
  const playbackRate = compartilhada ? taxa : taxaLocal;
  const setPlaybackRate = compartilhada ? onTaxa : setTaxaLocal;

  const barHeights = useMemo(() => generateBarHeights(url || "default-seed", 28), [url]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
      localStorage.setItem(LS_NAME, playbackRate);
    }
  }, [playbackRate]);

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => {
        setIsPlaying(false);
      });
    }
  };

  const handleTimeUpdate = () => {
    // Enquanto estamos medindo a duracao (veja abaixo), o tempo corrente vai
    // para um valor absurdo de proposito. Mostrar isso na tela seria pior que
    // nao mostrar nada.
    if (medindoRef.current) return;

    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    const elemento = audioRef.current;
    if (!elemento) return;

    setIsLoading(false);

    if (Number.isFinite(elemento.duration) && elemento.duration > 0) {
      setDuration(elemento.duration);
      return;
    }

    /**
     * Audio gravado NO NAVEGADOR nao traz a duracao no cabecalho.
     *
     * O MediaRecorder escreve o webm em fluxo, sem saber onde vai terminar, e
     * o campo de duracao fica vazio. O navegador entao responde Infinity ate
     * alguem percorrer o arquivo — e era por isso que o balao mostrava 0:00
     * num audio que claramente tinha alguns segundos.
     *
     * O jeito de descobrir e mandar o player para um ponto impossivelmente
     * distante: ele varre o arquivo, descobre o fim de verdade e avisa em
     * "durationchange". Dai voltamos o cursor para o inicio.
     */
    medindoRef.current = true;

    const aoDescobrir = () => {
      if (!Number.isFinite(elemento.duration)) return;

      elemento.removeEventListener("durationchange", aoDescobrir);
      setDuration(elemento.duration);

      try {
        elemento.currentTime = 0;
      } catch (e) {
        /* alguns navegadores recusam o seek antes de carregar */
      }

      medindoRef.current = false;
    };

    elemento.addEventListener("durationchange", aoDescobrir);

    // Rede de seguranca: se o navegador nao responder, o player volta ao normal
    // em vez de ficar com o tempo congelado para sempre.
    setTimeout(() => {
      if (!medindoRef.current) return;
      elemento.removeEventListener("durationchange", aoDescobrir);
      medindoRef.current = false;
    }, 4000);

    try {
      elemento.currentTime = 1e101;
    } catch (e) {
      elemento.removeEventListener("durationchange", aoDescobrir);
      medindoRef.current = false;
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  };

  const handleWaveformClick = (e) => {
    if (!waveformRef.current || !audioRef.current || !duration) return;
    const rect = waveformRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickRatio = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = clickRatio * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const toggleRate = () => {
    let nextRate = 1;
    if (playbackRate === 1) nextRate = 1.5;
    else if (playbackRate === 1.5) nextRate = 2;
    else if (playbackRate === 2) nextRate = 1;
    setPlaybackRate(nextRate);
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const activeColor = "#00a884";
  const inactiveColor = "rgba(134, 150, 160, 0.4)";

  // Audio nao carrega assinatura: no WhatsApp do cliente ele chega solto, sem
  // o "Fulano:" que vai no texto. Entao, so aqui dentro do sistema, o audio que
  // saiu daqui mostra a foto de quem gravou — e assim a equipe sabe de quem e a
  // voz sem precisar ouvir. O cliente nao ve nada disso.
  const fotoDoAutor =
    autor?.profileImage
      ? `${getBackendUrl()}public/${autor.profileImage}`
      : "";

  const avatarSrc = fromMe ? fotoDoAutor : contact?.profilePicUrl || "";

  const iniciaisDoAutor = String(autor?.name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((parte) => parte[0])
    .join("")
    .toUpperCase();

  return (
    <div className={classes.container}>
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onWaiting={() => setIsLoading(true)}
        onPlaying={() => {
          setIsLoading(false);
          setIsPlaying(true);
        }}
        onCanPlay={() => setIsLoading(false)}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      />

      <div className={classes.avatarWrapper}>
        <Avatar
          src={avatarSrc}
          className={classes.avatar}
          title={fromMe && autor?.name ? `Enviado por ${autor.name}` : undefined}
        >
          {fromMe && iniciaisDoAutor ? iniciaisDoAutor : <Person />}
        </Avatar>
        <div className={classes.micBadge}>
          <Mic />
        </div>
      </div>

      <IconButton
        className={classes.playButton}
        onClick={handlePlayPause}
        disabled={isLoading}
        size="small"
      >
        {isLoading ? (
          <CircularProgress size={28} style={{ color: activeColor }} />
        ) : isPlaying ? (
          <Pause className={classes.playIcon} />
        ) : (
          <PlayArrow className={classes.playIcon} />
        )}
      </IconButton>

      <div className={classes.contentWrapper}>
        <div
          ref={waveformRef}
          className={classes.waveformContainer}
          onClick={handleWaveformClick}
          title="Clique para avançar/voltar"
        >
          {barHeights.map((h, idx) => {
            const barPercent = (idx / (barHeights.length - 1)) * 100;
            const isFilled = barPercent <= progressPercent;
            return (
              <span
                key={idx}
                className={classes.waveformBar}
                style={{
                  height: h,
                  backgroundColor: isFilled ? activeColor : inactiveColor,
                }}
              />
            );
          })}
        </div>

        <div className={classes.bottomRow}>
          <Typography className={classes.durationText}>
            {isPlaying || currentTime > 0
              ? formatAudioTime(currentTime)
              : formatAudioTime(duration)}
          </Typography>

          <button
            type="button"
            className={classes.speedButton}
            onClick={toggleRate}
            title="Alterar velocidade"
          >
            {playbackRate}x
          </button>
        </div>
      </div>
    </div>
  );
}